import React, { useEffect, useState } from "react";
import { Link } from "@/lib/router";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { COOKIE_BANNER } from "@/i18n/legal";
import { initAnalytics, startPresence } from "@/lib/analytics";

const STORAGE_KEY = "vornx_cookie_consent";

export const CookieConsent = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("vornx:cookie", { detail: { visible } }));
  }, [visible]);

  const decide = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() }));
    if (choice === "accept") { initAnalytics(); startPresence(); }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-consent-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-gold/20 bg-ink/95 backdrop-blur-xl"
        >
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 sm:gap-5 sm:px-6 sm:py-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div className="max-w-2xl">
              <h3 className="text-xs uppercase tracking-overline text-gold">{t(COOKIE_BANNER.title)}</h3>
              <p className="mt-1.5 text-[13px] leading-snug text-white/65 sm:mt-2 sm:text-sm sm:leading-relaxed">
                {t(COOKIE_BANNER.text)}{" "}
                <Link to="/cookies" className="text-gold underline-offset-2 hover:underline">
                  {t(COOKIE_BANNER.more)}
                </Link>
              </p>
            </div>
            <div className="flex shrink-0 gap-3">
              <button
                data-testid="cookie-reject-btn"
                onClick={() => decide("reject")}
                className="flex-1 rounded-none border border-white/25 px-6 py-2.5 text-xs uppercase tracking-widest text-white/80 transition-colors duration-300 hover:border-gold hover:text-gold lg:flex-none"
              >
                {t(COOKIE_BANNER.reject)}
              </button>
              <button
                data-testid="cookie-accept-btn"
                onClick={() => decide("accept")}
                className="flex-1 rounded-none bg-gold px-6 py-2.5 text-xs uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-gold-muted lg:flex-none"
              >
                {t(COOKIE_BANNER.accept)}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
